import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import { SimpleDateInputProps } from './types';
import { InputPattern, UnitText, SimpleDateInputContainer } from './styles';
import { ThemeContext } from '../ThemeProvider/ThemeProvider';
import Input from '../Input/Input';

const PATTERNS = {
  simpleDate: '__/____',
  fullDate: '__/__/____',
};

const LONG_MONTHS = [1, 3, 5, 7, 8, 10, 12];

/**
 * SimpleDateInput
 *
 * @param label
 * @param input
 * @param meta
 * @param dateType - 'simpleDate' (MM/YYYY) or 'fullDate' (DD/MM/YYYY). Default: 'simpleDate'
 * @param size - 'small' o 'standard'. Default: 'standard'
 * @param variant - 'underline', 'contained-label', 'top-aligned-label'. Default: 'underline'
 * @param placeholder
 * @param unit - Unit of measurement (e.g: "days", "months")
 * @param disabled
 * @param readOnly
 * @param showClearIcon - Default: true
 
 * @param helperText
 * @param testId
 * @param inputRef
 * @param isMobile
 * @param onChange
 * @param onBlur
 * @param onFocus
 * @param onMouseDown
 * @param type
 *
 * @example
 * // Simple format (MM/YYYY)
 * <SimpleDateInput
 *   label="Período"
 *   input={input}
 *   meta={meta}
 *   dateType="simpleDate"
 *   placeholder="MM/YYYY"
 * />
 *
 * // Full format (DD/MM/YYYY)
 * <SimpleDateInput
 *   label="Fecha de nacimiento"
 *   input={input}
 *   meta={meta}
 *   dateType="fullDate"
 *   placeholder="DD/MM/YYYY"
 * />
 *
 * @example
 * // Integration with react-final-form
 * <Field name="startDate">
 *   {({ input, meta }) => (
 *     <SimpleDateInput
 *       label="Fecha de inicio"
 *       input={input}
 *       meta={meta}
 *       dateType="simpleDate"
 *       onChange={(value) => {
 *         input.onChange(value);
 *         // Lógica adicional si es necesaria
 *       }}
 *     />
 *   )}
 * </Field>
 *
 * @description
 * Format behavior:
 * - Simple format (MM/YYYY): 12 → 12/, 1234 → 12/34, 123456 → 12/3456
 * - Full format (DD/MM/YYYY): 12 → 12/, 1234 → 12/34, 123456 → 12/34/56
 *
 * @description
 * Automatic validation on blur (onBlur):
 * - Months: Validate that it is between 1-12
 * - Days: Validate according to the month and year (including leap years)
 * - Years: Validate that it has at least 3 digits
 * - Invalid values: Replaced with default values of the current day
 *
 * @description
 * Keyboard handling:
 * - Permitted keys: Numbers (0-9), navigation (Tab, Enter, Escape, Arrow keys), editing (Backspace, Delete)
 * - Special behavior: Backspace over "/" deletes the previous number, Delete over "/" deletes the next number
 * - Automatic format: Applied while typing
 */

const SimpleDateInput: React.FC<SimpleDateInputProps> = (props) => {
  const {
    input,
    meta,
    placeholder,
    label,
    readOnly,
    disabled,
    testId,
    size,
    variant = 'underline',
    helperText,
    showClearIcon = true,
    unit,
    dateType = 'simpleDate',
    onChange,
    onBlur,
    onFocus,
    onMouseDown,
    type,
    inputRef,
    isMobile,
  } = props;
  const { actualTheme, isMobile: mobileTheme } = useContext(ThemeContext);
  const useMobileVersion = useMemo(() => isMobile ?? mobileTheme, [isMobile, mobileTheme]);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const localInputRef: React.Ref<HTMLInputElement> = inputRef || useRef(null);

  const [internalValue, setInternalValue] = useState('');
  const [onChangeFlag, setonChangeFlag] = useState(false);
  const hasError = useMemo(() => meta && meta.invalid && meta.touched, [meta]);
  const inputSize = useMemo(() => {
    if (!size && useMobileVersion) return 'small';
    return size || 'standard';
  }, [size, useMobileVersion]);
  const inputHeight = useMemo(() => {
    const s = inputSize || 'standard';
    const h = actualTheme.input.sizes[s].height[variant];
    if (variant === 'top-aligned-label') return h + 20;
    return h;
  }, [actualTheme, inputSize, variant]);
  const inputValue = useMemo(() => input?.value || internalValue, [input?.value, internalValue]);
  const pattern = useMemo(() => PATTERNS[dateType].slice(inputValue?.length || 0, 11), [
    dateType,
    inputValue?.length,
  ]);

  const completeDoubleDigit = (value: number) => value.toString().padStart(2, '0');

  const currentDay = useMemo(() => completeDoubleDigit(new Date().getDate()), []);
  const currentMonth = useMemo(() => completeDoubleDigit(new Date().getMonth() + 1), []);
  const currentYear = useMemo(() => new Date().getFullYear().toString(), []);

  const isValidMonth = useCallback(
    (m: number) =>
      (Number.isInteger(m) && m >= 1 && m < 13) || (dateType === 'fullDate' && Number.isNaN(m)),
    [dateType],
  );
  const isValidYear = (y: number) => (Number.isInteger(y) && y >= 999) || Number.isNaN(y);

  const handleClear = useCallback(() => {
    if (input) {
      input.onChange('');
    }
    if (onChange) {
      onChange('');
    }
  }, [input, onChange]);

  const handleSimpleDateBlur = useCallback(() => {
    if (!input || !onChangeFlag) return;
    let localValue = internalValue;

    const [monthStr, yearStr] = internalValue.split('/');
    const month = Number(monthStr);
    const year = Number(yearStr);
    let monthResult = Number.isNaN(month) || month === 0 ? Number(currentMonth) : month;
    const yearResult = Number.isNaN(year) || !isValidYear(year) ? Number(currentYear) : year;
    monthResult = !isValidMonth(monthResult) ? 12 : monthResult;

    localValue = `${completeDoubleDigit(monthResult)}/${yearResult}`;

    if (localValue) input.onChange(localValue);
    input.onBlur();
    if (onBlur) onBlur();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalValue, input, currentMonth, currentYear, onBlur]);

  const isLeapYear = (year: number) =>
    // eslint-disable-next-line no-nested-ternary
    year % 400 === 0 ? true : year % 100 === 0 ? false : year % 4 === 0;

  const getValidDay = (day: number, month: number, year: number) => {
    if (day <= 28) {
      return day;
    }
    let maxDay = 31;
    if (!LONG_MONTHS.includes(month)) {
      if (month !== 2) {
        maxDay = 30;
      } else {
        maxDay = isLeapYear(year) ? 29 : 28;
      }
    }
    return day <= maxDay ? day : maxDay;
  };

  const handleFullDateBlur = useCallback(() => {
    if (!input || !onChangeFlag) return;
    let localValue = internalValue;

    const [dayStr, monthStr, yearStr] = internalValue.split('/');

    const day = Number(dayStr);
    const month = Number(monthStr);
    const year = Number(yearStr);

    let dayResult = Number.isNaN(day) || day === 0 ? Number(currentDay) : day;
    let monthResult = Number.isNaN(month) || month === 0 ? Number(currentMonth) : month;
    const yearResult = Number.isNaN(year) || !isValidYear(year) ? Number(currentYear) : year;

    monthResult = !isValidMonth(monthResult) ? 12 : monthResult;
    dayResult = getValidDay(dayResult, monthResult, yearResult);

    localValue = `${completeDoubleDigit(dayResult)}/${completeDoubleDigit(
      monthResult,
    )}/${yearResult}`;

    if (localValue) input.onChange(localValue);
    input.onBlur();
    if (onBlur) onBlur();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalValue, input, currentDay, currentMonth, currentYear, onBlur]);

  const handlePatternClick = useCallback(() => {
    if (inputRef?.current) inputRef.current.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const inputElem = event.target as HTMLInputElement;
      const { selectionStart, selectionEnd, value } = inputElem;
      // Teclas permitidas
      if (
        event.key === 'Tab' ||
        event.key === 'Enter' ||
        event.key === 'Escape' ||
        event.key === 'ArrowLeft' ||
        event.key === 'ArrowRight' ||
        event.key === 'ArrowUp' ||
        event.key === 'ArrowDown' ||
        event.ctrlKey ||
        event.metaKey
      ) {
        return;
      }

      // Borrado especial para Backspace y Delete sobre la barra
      if (
        event.key === 'Backspace' &&
        selectionStart === selectionEnd &&
        selectionStart !== null &&
        selectionStart > 0
      ) {
        // Si el cursor está justo después de una barra
        if (value[selectionStart - 1] === '/') {
          event.preventDefault();
          // Eliminar la barra y el número anterior
          const before = value.slice(0, selectionStart - 2);
          const after = value.slice(selectionStart);
          const newValue = before + after;
          setInternalValue(newValue);
          if (input) input.onChange(newValue);
          if (onChange) onChange(newValue);
          // Mover el cursor correctamente
          setTimeout(() => {
            if (inputElem.setSelectionRange) {
              inputElem.setSelectionRange(selectionStart - 1, selectionStart - 1);
            }
          }, 0);
          return;
        }
      }
      if (
        event.key === 'Delete' &&
        selectionStart === selectionEnd &&
        selectionStart !== null &&
        selectionStart < value.length
      ) {
        // Si el cursor está justo antes de una barra
        if (value[selectionStart] === '/') {
          event.preventDefault();
          // Eliminar la barra y el número siguiente
          const before = value.slice(0, selectionStart);
          const after = value.slice(selectionStart + 2);
          const newValue = before + after;
          setInternalValue(newValue);
          if (input) input.onChange(newValue);
          if (onChange) onChange(newValue);
          // Mover el cursor correctamente
          setTimeout(() => {
            if (inputElem.setSelectionRange) {
              inputElem.setSelectionRange(selectionStart, selectionStart);
            }
          }, 0);
          return;
        }
      }

      // Solo permitir números y borrado
      if (event.key === 'Backspace' || event.key === 'Delete') {
        return;
      }
      if (!/^[0-9]$/.test(event.key)) {
        event.preventDefault();
      }
    },
    [input, onChange],
  );

  const handleKeyPress = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    // Teclas permitidas
    if (
      event.key === 'Backspace' ||
      event.key === 'Delete' ||
      event.key === 'Tab' ||
      event.key === 'Enter' ||
      event.key === 'Escape' ||
      event.key === 'ArrowLeft' ||
      event.key === 'ArrowRight' ||
      event.key === 'ArrowUp' ||
      event.key === 'ArrowDown' ||
      event.ctrlKey ||
      event.metaKey
    ) {
      return;
    }

    // Solo permitir números
    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
    }
  }, []);

  const handleInputChange = useCallback(
    (val: string | number) => {
      setonChangeFlag(true);
      const stringVal = String(val);

      // Si el valor está vacío, permitir limpiar
      if (stringVal === '') {
        setInternalValue('');
        if (input) input.onChange('');
        if (onChange) onChange('');
        return;
      }

      // Extraer solo los números del valor
      const numbers = stringVal.replace(/[^0-9]/g, '');

      // Si no hay números, no hacer nada
      if (numbers === '') {
        return;
      }

      // Construir el valor formateado
      let formattedValue = numbers;

      if (dateType === 'simpleDate') {
        // Para MM/YYYY - limitar año a 4 dígitos
        if (numbers.length >= 2) {
          const month = numbers.slice(0, 2);
          const year = numbers.slice(2, 6); // Máximo 4 dígitos para el año
          formattedValue = `${month}/${year}`;
        }
      } else if (dateType === 'fullDate') {
        // Para DD/MM/YYYY - limitar año a 4 dígitos
        if (numbers.length >= 4) {
          const day = numbers.slice(0, 2);
          const month = numbers.slice(2, 4);
          const year = numbers.slice(4, 8); // Máximo 4 dígitos para el año
          formattedValue = `${day}/${month}/${year}`;
        } else if (numbers.length >= 2) {
          formattedValue = `${numbers.slice(0, 2)}/${numbers.slice(2)}`;
        }
      }

      // Actualizar el estado
      setInternalValue(formattedValue);
      if (input) input.onChange(formattedValue);
      if (onChange) onChange(formattedValue);
    },
    [input, onChange, dateType],
  );

  useEffect(() => {
    setInternalValue(input.value);
  }, [input.value]);

  return (
    <ThemeProvider theme={actualTheme}>
      <SimpleDateInputContainer>
        <Input
          ref={localInputRef}
          testId={testId}
          label={label}
          readOnly={readOnly || disabled}
          disabled={disabled}
          value={inputValue}
          input={input}
          meta={meta}
          autoComplete="off"
          size={size}
          variant={variant}
          helperText={helperText}
          showClearIcon={showClearIcon}
          placeholder={placeholder || ' '}
          onChange={handleInputChange}
          onClear={handleClear}
          rightAddOn={
            unit && (
              <UnitText size={size} hasError={hasError} data-testid={`${testId}-unit`}>
                {unit}
              </UnitText>
            )
          }
          onBlur={dateType === 'simpleDate' ? handleSimpleDateBlur : handleFullDateBlur}
          onFocus={onFocus}
          onMouseDown={onMouseDown}
          type={type}
          isMobile={useMobileVersion}
          targetHeight={inputHeight}
          onKeyDown={handleKeyDown}
          onKeyPress={handleKeyPress}
        />
        {(inputValue?.length > 0 || !placeholder) && (
          <InputPattern
            variant={variant}
            hasError={hasError}
            size={size}
            isFullDate={dateType === 'fullDate'}
            isMobile={useMobileVersion}
            onClick={handlePatternClick}
          >
            {pattern}
          </InputPattern>
        )}
      </SimpleDateInputContainer>
    </ThemeProvider>
  );
};

export default SimpleDateInput;
